import { isFullyIslandedIntent } from '../intent/gridScope';
/**
 * Intent Intelligence — SLICE 2: Next Best Input.
 *
 * `rankNextInputs(intent, site, sed?)` answers a single product question:
 *   "Of all the Project-Intent fields the user has NOT yet decided, which one
 *    should they fill in NEXT — and why?"
 *
 * It is a PURE, DETERMINISTIC composition of existing primitives — NO LLM,
 * no store reads, no Firestore, no side effects. Same inputs → same output.
 *
 * It composes four already-validated layers (REUSE-first; nothing here
 * re-derives signal that another module already owns):
 *
 *   1. WHAT'S MISSING + WHAT'S CRITICAL
 *      `intentCriticalScope(intent)` (computeOverviewFindingCards) tells us
 *      which report types / coverage domains the declared intent makes
 *      critical. We use the *shape* of that scope (does nuclear/cooling/
 *      sustainability widen it?) to know which fields unlock the most
 *      downstream analysis when answered.
 *
 *   2. DECISION VALUE OF FILLING THE FIELD
 *      `providerImpactForIntent(provider, intent)` + `SEVERITY_ORDER`
 *      (providerImpactForIntent) score how much a field's *current ambiguity*
 *      degrades the report. A field whose ambiguity demotes a `critical`
 *      provider to `minor`/`irrelevant` is worth more to resolve than one
 *      that changes nothing.
 *
 *   3. INFERABILITY (can we suggest a value NOW, no LLM?)
 *      `suggestProjectIntent(...)` (the deterministic 6-tier suggester) tells
 *      us whether we can pre-fill the field from prior reports / docs / site
 *      context / archetype. An inferable field is cheaper for the user to
 *      confirm than to author cold, so it ranks slightly ahead at equal value.
 *
 *   4. LIFECYCLE ORDER + FIELD DEPENDENCIES
 *      A sensible authoring order (mirrors ProjectIntentForm / the report
 *      lifecycle) breaks ties so suggestions feel natural. DEPENDENCIES are a
 *      HARD gate: we never surface `reactorDesignClass` before the user has
 *      said nuclear is in play, nor PUE / power-factor / BoP heat-rejection
 *      before a power figure exists. Gated fields are simply omitted from the
 *      ranking until their prerequisite is decided.
 *
 * USER ALWAYS WINS: a field whose `intent.fieldSource[field]` is `'user'` or
 * `'accepted'` is treated as decided even if its value looks like a default —
 * we never nag the user to re-enter something they authored. The deliberate
 * exception is `'user-unknown'`: the user's answer is preserved and labelled,
 * but the unresolved fact remains an actionable follow-up.
 */
import type { ProjectIntent } from '../../types/siteRag';
import { hasDeclaredWaterSource } from '../intent/waterSource';
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import {
  providerImpactForIntent,
  SEVERITY_ORDER,
  type ProviderId,
  type ImpactSeverity,
} from '../providerImpactForIntent';
import { intentCriticalScope } from '../overview/computeOverviewFindingCards';
import {
  suggestProjectIntent,
  type SuggestedProjectIntent,
} from '../suggestProjectIntent';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';

/* ── Field catalogue ────────────────────────────────────────────────── */

/**
 * The intent fields this ranker considers. Deliberately the decision-shaping
 * subset of `ProjectIntent` — not every key. Phasing / customWeights /
 * provenance are managed by their own surfaces and are out of scope here.
 */
export type RankableIntentField =
  | 'workloadProfile'
  | 'powerRequirement'
  | 'coolingStrategy'
  | 'nuclearInterest'
  | 'priority'
  | 'timeline'
  | 'reactorDesignClass'
  | 'pueValue'
  | 'powerFactor'
  | 'bopHeatRejection'
  // ── Six net-new BTM intent fields (field-architecture audit §3.6/§4). These
  // are decision-relevant distance-to-GO inputs; ranking them is what lets the
  // path and the verdict recognise the SAME set (they were Path to GO's
  // "decide next" rows until that stage was deleted).
  | 'interconnectionStatus'
  | 'waterSource'
  | 'fuelAccess'
  | 'offtake'
  | 'capacityCreditIntent'
  | 'carbonCommitment';

/** A single ranked "what to fill next" recommendation. */
export interface NextInputRecommendation {
  /** Which intent field to fill next. */
  field: RankableIntentField;
  /** Short, user-facing reason this field is worth answering now. */
  reason: string;
  /**
   * A concrete value we can pre-fill RIGHT NOW (deterministic suggester),
   * when one exists. Absent when nothing better than the platform fallback
   * is available — the user has to decide cold.
   */
  suggestedValue?: string;
  /**
   * Decision value of resolving this field, 0–100. Higher = filling it
   * unlocks / de-risks more of the report. Composite of critical-scope
   * weight + provider-impact severity + inferability bonus + lifecycle order.
   */
  valueScore: number;
  /** True when the deterministic suggester can offer a non-fallback value. */
  inferable: boolean;
}

/** Full ranking result. `best` is the single top recommendation. */
export interface NextInputRanking {
  /** Highest-value next field, or null when every in-scope field is decided. */
  best: NextInputRecommendation | null;
  /** All in-scope undecided fields, highest value first. Includes `best`. */
  ranked: NextInputRecommendation[];
  /**
   * In-scope fields the user recorded as UNKNOWN, in lifecycle order. Recording
   * "unknown" is the user's answer, so these are never ranked or asked again.
   * The fact stays unknown: the diligence follow-up travels with the intent
   * into the SED and the report prompts (`explicitUnknownIntentFields`).
   */
  recordedUnknown: RankableIntentField[];
}

/* ── Lifecycle order (tiebreak) ─────────────────────────────────────── */

/**
 * Sensible authoring order — mirrors ProjectIntentForm's section flow and the
 * report lifecycle (workload → scale → cooling → energy strategy → drivers →
 * timing → refinements). Lower index = earlier in the natural conversation,
 * which we prefer at equal decision value. Fields absent here sort last.
 */
const LIFECYCLE_ORDER: readonly RankableIntentField[] = [
  'workloadProfile',
  'powerRequirement',
  'coolingStrategy',
  'nuclearInterest',
  'priority',
  'timeline',
  // BTM development-readiness fields slot after the spine/strategy fields, in
  // rough development-diligence order (control → grid → water → fuel → revenue →
  // firm-capacity target → carbon).
  'interconnectionStatus',
  'waterSource',
  'fuelAccess',
  'offtake',
  'capacityCreditIntent',
  'carbonCommitment',
  'reactorDesignClass',
  'pueValue',
  'powerFactor',
  'bopHeatRejection',
] as const;

function lifecycleIndex(field: RankableIntentField): number {
  const i = LIFECYCLE_ORDER.indexOf(field);
  return i < 0 ? LIFECYCLE_ORDER.length : i;
}

/* ── Provider mapping (for decision value) ──────────────────────────── */

/**
 * Map a rankable field to the SED provider whose impact most directly turns
 * on it. Used to read `providerImpactForIntent` — a field that gates a
 * high-severity provider is worth more to resolve. Fields with no single
 * provider proxy (workload, priority, timeline, scale, refinements) return
 * null and fall back to the critical-scope / lifecycle signal alone.
 */
const FIELD_PROVIDER_PROXY: Partial<Record<RankableIntentField, ProviderId>> = {
  coolingStrategy: 'waterStress',
  nuclearInterest: 'nuclearInfra',
  reactorDesignClass: 'nuclearInfra',
};

/* ── Decided / undecided predicate (USER ALWAYS WINS) ───────────────── */

/**
 * Treat a field as DECIDED when the user authored a concrete value, OR when it carries a
 * concrete (non-`undecided`, non-empty) value. We never re-surface a field a
 * user has explicitly set — provenance wins over value inspection.
 */
function isFieldDecided(intent: ProjectIntent, field: RankableIntentField): boolean {
  const provenance = intent.fieldSource?.[field as keyof NonNullable<ProjectIntent['fieldSource']>];
  // An explicit unknown is an ANSWERED question, but not a known fact: the
  // value check below must not read its sentinel as decided. `rankNextInputs`
  // routes it to `recordedUnknown` before calling this.
  if (isIntentFieldExplicitlyUnknown(intent, field)) return false;
  if (provenance === 'user' || provenance === 'accepted') return true;

  switch (field) {
    case 'workloadProfile':
      // 'mixed' is a real choice, but it is also the universal fallback; treat
      // an unset workload as undecided. Any explicit profile counts as decided.
      return intent.workloadProfile != null;
    case 'powerRequirement':
      return (
        (intent.phases?.some(phase => (Number.isFinite(phase.capacityMW) && (phase.capacityMW ?? 0) > 0)
          || (phase.capacityBucket != null && phase.capacityBucket !== 'undecided')) ?? false) ||
        (typeof intent.powerRequirementMW === 'number' && Number.isFinite(intent.powerRequirementMW) && intent.powerRequirementMW > 0) ||
        (intent.powerRequirement != null && intent.powerRequirement !== 'undecided')
      );
    case 'coolingStrategy':
      return intent.coolingStrategy != null && intent.coolingStrategy !== 'undecided';
    case 'nuclearInterest':
      return intent.nuclearInterest != null && intent.nuclearInterest !== 'undecided';
    case 'priority':
      return intent.priority != null && intent.priority !== 'undecided';
    case 'timeline':
      return (
        (intent.phases?.some(phase => !!phase.targetInServiceDate?.trim()
          || (phase.codTimeline != null && phase.codTimeline !== 'undecided')) ?? false) ||
        (typeof intent.inServiceDate === 'string' && intent.inServiceDate.length > 0) ||
        (intent.timeline != null && intent.timeline !== 'undecided')
      );
    case 'reactorDesignClass':
      return intent.reactorDesignClass != null;
    case 'pueValue':
      return typeof intent.pueValue === 'number' && intent.pueValue > 0;
    case 'powerFactor':
      return typeof intent.powerFactor === 'number' && intent.powerFactor > 0;
    case 'bopHeatRejection':
      return intent.bopHeatRejection != null && intent.bopHeatRejection !== 'unknown';
    // ── BTM fields. Treated as DECIDED once the operator has set the field to a
    // concrete (non-undecided / non-'none'-default) state. 'undecided' / absent
    // is the open state we surface as "decide next". A declared 'none' / merchant
    // / etc. is a real decision (the operator answered) and counts as decided.
    case 'interconnectionStatus':
      return intent.interconnectionStatus?.queueStatus != null;
    case 'waterSource':
      return hasDeclaredWaterSource(intent.waterSource);
    case 'fuelAccess':
      return intent.fuelAccess?.supply != null;
    case 'offtake':
      return intent.offtake?.structure != null;
    case 'capacityCreditIntent':
      return intent.capacityCreditIntent?.target != null;
    case 'carbonCommitment':
      return intent.carbonCommitment != null;
    default: {
      const _exhaustive: never = field;
      void _exhaustive;
      return false;
    }
  }
}

/* ── Dependency gate ────────────────────────────────────────────────── */

/**
 * Hard prerequisite gate. A field is only offerable once its dependency is
 * decided AND (where relevant) resolved to a value that makes the dependent
 * field meaningful. Returns true when the field may be surfaced.
 *
 *   - reactorDesignClass    → requires nuclearInterest to be active
 *                             (primary | considering). No point asking the
 *                             reactor class before the user wants nuclear.
 *   - pueValue              → requires a power figure (powerRequirement decided).
 *   - powerFactor           → requires a power figure.
 *   - bopHeatRejection      → requires a power figure.
 *   - capacityCreditIntent  → moot on a declared off-grid island: BTM-only
 *                             strategy + interconnection queue explicitly
 *                             'none' means there is no market to accredit
 *                             firm capacity into, so the ELCC target is not
 *                             a decision the operator can meaningfully take.
 *                             (Hybrid / grid-connected BTM keeps the field —
 *                             accreditation still applies there.)
 */
function dependencyMet(
  intent: ProjectIntent,
  field: RankableIntentField,
  btmStrategy?: 'grid-only' | 'btm-only' | 'hybrid' | 'islanded',
): boolean {
  switch (field) {
    case 'reactorDesignClass':
      return intent.nuclearInterest === 'primary' || intent.nuclearInterest === 'considering';
    case 'pueValue':
    case 'powerFactor':
    case 'bopHeatRejection':
      return isFieldDecided(intent, 'powerRequirement');
    case 'capacityCreditIntent':
      if (isFullyIslandedIntent(intent, btmStrategy)) return false;
      return !(
        btmStrategy === 'btm-only' &&
        intent.interconnectionStatus?.queueStatus === 'none'
      );
    default:
      return true;
  }
}

/* ── Inferability + suggested-value formatting ──────────────────────── */

/**
 * The fields the deterministic suggester can pre-fill. The original three
 * spine fields are joined (#34) by the four DECIDE-NEXT BTM decision fields —
 * `capacityCreditIntent` (ELCC target), `offtake` (PPA structure),
 * `carbonCommitment`, and `reactorDesignClass` — now that
 * `suggestProjectIntent` emits a tiered suggestion for each. This lets the
 * DECIDE-NEXT nudge OFFER a value for them instead of only a 'decide' link.
 */
type SuggestableField =
  | 'workloadProfile'
  | 'nuclearInterest'
  | 'priority'
  | 'capacityCreditIntent'
  | 'offtake'
  | 'carbonCommitment'
  | 'reactorDesignClass';

function isSuggestable(field: RankableIntentField): field is SuggestableField {
  return (
    field === 'workloadProfile' ||
    field === 'nuclearInterest' ||
    field === 'priority' ||
    field === 'capacityCreditIntent' ||
    field === 'offtake' ||
    field === 'carbonCommitment' ||
    field === 'reactorDesignClass'
  );
}

/**
 * Resolve `{ inferable, suggestedValue }` for a field from the deterministic
 * suggester. A field is "inferable" only when the suggester returns a
 * non-`fallback` tier — a fallback means we have no real signal, so we do not
 * claim to have inferred anything. The four BTM fields (#34) are OPTIONAL on
 * `SuggestedProjectIntent`: an absent suggestion is treated exactly like a
 * fallback (not inferable, no value).
 */
function inferenceFor(
  field: RankableIntentField,
  suggestions: SuggestedProjectIntent,
): { inferable: boolean; suggestedValue?: string } {
  if (!isSuggestable(field)) return { inferable: false };
  const s = suggestions[field];
  if (s == null || s.source === 'fallback') return { inferable: false };
  return { inferable: true, suggestedValue: String(s.value) };
}

/* ── Decision-value scoring ─────────────────────────────────────────── */

/**
 * Base decision value per field, before provider-impact / inferability /
 * lifecycle adjustment. Anchored on how much of the report's spine each field
 * controls:
 *   - power + workload steer EVERY downstream section → highest base.
 *   - cooling + nuclear branch large swaths of analysis → high base.
 *   - priority + timeline shape framing / scheduling   → medium base.
 *   - PUE / power-factor / BoP are refinements          → low base.
 */
const FIELD_BASE_VALUE: Record<RankableIntentField, number> = {
  powerRequirement: 60,
  workloadProfile: 55,
  coolingStrategy: 45,
  nuclearInterest: 45,
  // BTM development-readiness fields — high base: land control and firm-capacity
  // target are GO-gating, the rest are classic distance-to-GO diligence items.
  capacityCreditIntent: 47,
  interconnectionStatus: 44,
  waterSource: 42,
  fuelAccess: 40,
  offtake: 38,
  carbonCommitment: 36,
  priority: 35,
  timeline: 35,
  reactorDesignClass: 25,
  pueValue: 20,
  powerFactor: 12,
  bopHeatRejection: 18,
};

/**
 * Map provider severity to a small value bump. Kept deliberately modest
 * (max +9) so it REFINES the ranking within a base-value tier rather than
 * leapfrogging the report-spine fields (power / workload). A field that
 * gates a `critical` provider edges ahead of a same-base peer, but does not
 * outrank a structurally higher-value field.
 */
function severityBump(severity: ImpactSeverity): number {
  // SEVERITY_ORDER: irrelevant 0, minor 1, significant 2, critical 3.
  return SEVERITY_ORDER[severity] * 3; // 0, 3, 6, 9
}

/**
 * Compute the 0–100 decision value of resolving `field` given the current
 * intent. Pure: depends only on the inputs passed in.
 */
function scoreField(
  field: RankableIntentField,
  intent: ProjectIntent,
  scopeWeight: number,
  inferable: boolean,
): number {
  let score = FIELD_BASE_VALUE[field];

  // Provider-impact contribution — a field gating a high-severity provider
  // (e.g. cooling on a wet-cooled project, nuclear when active) is worth more.
  const proxy = FIELD_PROVIDER_PROXY[field];
  if (proxy) {
    score += severityBump(providerImpactForIntent(proxy, intent).severity);
  }

  // Critical-scope contribution — fields that widen the intent-critical scope
  // (nuclear, cooling, sustainability priority) unlock more analysis.
  score += scopeWeight;

  // Inferability bonus — a field we can pre-fill now is cheaper to confirm
  // than to author cold, so nudge it up a little (kept small so high-value
  // un-inferable fields still win).
  if (inferable) score += 5;

  // Earlier lifecycle steps get a small bump so, at equal substantive value,
  // the natural-order field wins. Magnitude < the smallest base-value gap so
  // it only ever breaks near-ties.
  score += (LIFECYCLE_ORDER.length - lifecycleIndex(field)) * 0.5;

  return Math.round(Math.max(0, Math.min(100, score)) * 100) / 100;
}

/* ── Reason copy ────────────────────────────────────────────────────── */

function reasonFor(
  field: RankableIntentField,
  intent: ProjectIntent,
  inferable: boolean,
): string {
  const proxy = FIELD_PROVIDER_PROXY[field];
  const impactReason = proxy ? providerImpactForIntent(proxy, intent).reason : null;

  const base: Record<RankableIntentField, string> = {
    powerRequirement:
      'Power target scales every downstream estimate — load, interconnection, LCOE, and equipment sizing.',
    workloadProfile:
      'Workload profile sets the cooling design basis, power density, and which report sections carry weight.',
    coolingStrategy:
      impactReason ?? 'Cooling strategy decides whether water availability gates feasibility.',
    nuclearInterest:
      impactReason ?? 'Nuclear stance decides whether NRC / EPZ analysis and licensing pathway apply.',
    priority:
      'Priority steers scoring weights and narrative framing toward what the project optimises for.',
    timeline:
      'In-service date drives critical-path scheduling, procurement lead times, and interconnection sequencing.',
    reactorDesignClass:
      'Reactor design class refines the licensing pathway and EPZ basis now that nuclear is in play.',
    pueValue:
      'PUE converts IT load to facility / grid demand — pin it to firm up the power-stack numbers.',
    powerFactor:
      'Power factor sets the apparent-power (MVA) rating basis for transformers and switchgear.',
    bopHeatRejection:
      'Balance-of-plant heat-rejection method refines the PUE estimate and waste-heat credit eligibility.',
    interconnectionStatus:
      'The utility load-service or generator-interconnection milestone informs schedule risk; an accepted load-service agreement does not complete commissioning or a separate generator request.',
    waterSource:
      'Water source + rights status gates cooling-water availability and drought-curtailment exposure.',
    fuelAccess:
      'Gas / fuel deliverability is a long-lead, single-path COD dependency for any on-site firm gas generation.',
    offtake:
      'Record the known revenue arrangement during commercial planning. Unknown is acceptable for screening; a preference is not evidence of a signed PPA.',
    capacityCreditIntent:
      'The firm-capacity (ELCC) accreditation target is the headline BTM metric the firm-capacity GO/No-Go grades against.',
    carbonCommitment:
      'A carbon commitment (RE100 / 24-7-CFE / net-zero) constrains which power technologies are allowed on the path to GO.',
  };

  const reason = base[field];
  if (inferable) {
    return `${reason} A suggested value is available to confirm.`;
  }
  return reason;
}

/* ── Scope-weight derivation ────────────────────────────────────────── */

/**
 * How much each field would change the intent-critical scope. We compare the
 * scope under the current intent against the scope with the field resolved to
 * its scope-widening value, and award points proportional to the report-types
 * / domains it unlocks. Purely structural — reads `intentCriticalScope` only.
 */
function scopeWeightFor(field: RankableIntentField, intent: ProjectIntent): number {
  const current = intentCriticalScope(intent);
  const currentSize = current.reportTypes.length + current.domains.length;

  // Construct the "if decided to its scope-widening value" intent and measure
  // how much bigger the critical scope becomes. We only model the fields whose
  // values demonstrably widen scope in `intentCriticalScope`.
  let probe: ProjectIntent | null = null;
  switch (field) {
    case 'nuclearInterest':
      probe = { ...intent, nuclearInterest: 'primary' };
      break;
    case 'coolingStrategy':
      probe = { ...intent, coolingStrategy: 'liquid' };
      break;
    case 'priority':
      probe = { ...intent, priority: 'sustainability' };
      break;
    default:
      return 0;
  }

  const widened = intentCriticalScope(probe);
  const widenedSize = widened.reportTypes.length + widened.domains.length;
  const delta = Math.max(0, widenedSize - currentSize);
  // Modest per-unit weight (matches `severityBump`'s magnitude) so scope
  // widening REFINES order within a tier without leapfrogging the spine
  // fields (power / workload). Each unlocked report-type / domain = 2 points.
  return delta * 2;
}

/* ── Public entry point ─────────────────────────────────────────────── */

/**
 * Rank the unfilled / undecided Project-Intent fields by how valuable it would
 * be to answer each next. Pure + deterministic — given the same inputs the
 * output is byte-stable (ties resolve by the fixed lifecycle order).
 *
 * @param intent  the current ProjectIntent (the single source of truth).
 * @param site    the site (used to derive archetype for the inferability layer).
 * @param sed     optional SED — accepted for forward-compatibility / parity
 *                with sibling intent-intelligence helpers; the current ranking
 *                logic does not depend on it (kept side-effect free).
 * @returns       `{ best, ranked }`. When every in-scope field is decided,
 *                `best` is null and `ranked` is empty.
 */
export function rankNextInputs(
  intent: ProjectIntent | null | undefined,
  site?: SiteEntity | null,
  sed?: SiteEvidenceDocument | null,
  options: { stage?: 'screening' | 'detailed' } = {},
): NextInputRanking {
  void sed; // reserved — see docstring.

  if (!intent) {
    return { best: null, ranked: [], recordedUnknown: [] };
  }

  // Deterministic suggester — drives the inferability layer. We feed it the
  // archetype (from the site) so it stays pure and no-LLM, PLUS the USER's
  // already-declared nuclear stance / priority (#34) as hints so the BTM
  // DECIDE-NEXT fields (reactor class / ELCC / offtake / carbon) infer from
  // the stance the user actually set — not only from an archetype guess.
  // Richer context (prior reports / docs) is the caller's job to plumb if
  // desired, but is not required for a sensible ranking.
  const suggestions = suggestProjectIntent({
    site: site ?? undefined,
    archetype: site?.archetypeOverride,
    ...(intent.nuclearInterest && intent.nuclearInterest !== 'undecided'
      ? { nuclearInterestHint: intent.nuclearInterest }
      : {}),
    ...(intent.priority && intent.priority !== 'undecided'
      ? { priorityHint: intent.priority }
      : {}),
  });

  const recommendations: NextInputRecommendation[] = [];
  const recordedUnknown: RankableIntentField[] = [];

  // Accountable grid-vs-BTM strategy (SSOT: PowerAvailabilityPreferences on the
  // site's typed-preferences defaults — NOT a ProjectIntent field). Feeds the
  // capacityCreditIntent dependency gate: off-grid islands have no market to
  // accredit into.
  const btmStrategy = site?.typedPreferencesDefaults?.['power-availability']?.btmStrategy;

  const screeningFields: ReadonlySet<RankableIntentField> = new Set(['workloadProfile', 'powerRequirement', 'timeline']);
  for (const field of LIFECYCLE_ORDER) {
    // Early screening compares designs; commercial and engineering choices belong
    // to the detailed editor/report setup, even if a legacy site saved a strategy.
    if (options.stage !== 'detailed' && !screeningFields.has(field)) continue;
    // Skip decided fields (USER ALWAYS WINS via provenance + value check).
    if (isFieldDecided(intent, field)) continue;
    // Skip fields whose prerequisite is not yet decided (dependency gate).
    if (!dependencyMet(intent, field, btmStrategy)) continue;
    // A recorded unknown is the user's answer — never ask for it again.
    if (isIntentFieldExplicitlyUnknown(intent, field)) {
      recordedUnknown.push(field);
      continue;
    }

    const { inferable, suggestedValue } = inferenceFor(field, suggestions);
    const scopeWeight = scopeWeightFor(field, intent);
    const valueScore = scoreField(field, intent, scopeWeight, inferable);
    const reason = reasonFor(field, intent, inferable);

    recommendations.push({
      field,
      reason,
      ...(suggestedValue != null ? { suggestedValue } : {}),
      valueScore,
      inferable,
    });
  }

  // Sort: highest value first; ties broken by lifecycle order (stable, so the
  // earlier-declared field wins). Deterministic for identical inputs.
  recommendations.sort((a, b) => {
    if (b.valueScore !== a.valueScore) return b.valueScore - a.valueScore;
    return lifecycleIndex(a.field) - lifecycleIndex(b.field);
  });

  return {
    best: recommendations.length > 0 ? recommendations[0]! : null,
    ranked: recommendations,
    recordedUnknown,
  };
}
